import { Switch as SwitchPrimitive } from 'radix-ui'
import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * 36 × 20 with 4px corners and a square 14px thumb, 2px inset; never a pill.
 * Off is a quiet `--muted` track with an `--input` boundary and thumb, both at
 * the 3:1 boundary floor; on is an ink track with a paper thumb. Disabled is
 * recoloured, never faded.
 *
 * Reach for this only for a setting that takes effect immediately. Anything
 * saved on submit is a Checkbox. The one named exception is the company role
 * editor (`apps/company/src/features/roles/`), which grants access with
 * Switches by product decision; it is not licence to use a Switch elsewhere.
 *
 * The thumb moves with a transform. The flex row is mirrored in Arabic, so
 * the thumb starts on the right there and the `rtl:` pair sends it left.
 */
function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 items-center rounded-sm border p-0.5 transition-ui',
        'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'data-[state=unchecked]:border-input data-[state=unchecked]:bg-muted',
        'enabled:data-[state=unchecked]:hover:border-foreground',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
        'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted data-[state=checked]:disabled:border-border data-[state=checked]:disabled:bg-muted',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block size-3.5 rounded-[2px] transition-transform duration-[var(--motion-fast)] ease-[var(--motion-ease)]',
          'data-[state=unchecked]:translate-x-0 data-[state=unchecked]:bg-input',
          'data-[state=checked]:translate-x-4 data-[state=checked]:bg-brand-foreground',
          'rtl:data-[state=checked]:-translate-x-4',
          'in-disabled:data-[state=unchecked]:bg-border in-disabled:data-[state=checked]:bg-muted-foreground',
        )}
      />
    </SwitchPrimitive.Root>
  )
}

export { Switch }
